import React, { useState, useEffect, useMemo } from 'react';

export type CosmicLoaderMode = 'astro' | 'general' | 'reading';

interface CosmicLoaderProps {
  isLoading: boolean;
  estimatedTime?: number;
  mode?: CosmicLoaderMode;
}

const CosmicLoader: React.FC<CosmicLoaderProps> = ({ isLoading, estimatedTime = 60, mode = 'general' }) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);

  const astroMessages = useMemo(
    () => [
      'Consulting the celestial bodies...',
      'Aligning planetary positions...',
      'Reading your cosmic blueprint...',
      'Channeling ancient wisdom...',
      'Interpreting the stars...',
      'Gathering celestial insights...',
      'Mapping your destiny...',
      "Decoding the universe's message...",
      'Connecting with cosmic energy...',
      'Unveiling hidden patterns...',
      'Synchronizing with the cosmos...',
      'Illuminating your path...'
    ],
    []
  );

  const generalMessages = useMemo(
    () => [
      'Searching the sacred texts...',
      'Channeling Vedic wisdom...',
      'Consulting ancient scriptures...',
      'Gathering spiritual insights...',
      'Exploring the Puranas...',
      'Unveiling timeless knowledge...',
      'Connecting with divine wisdom...',
      'Decoding sacred teachings...',
      'Illuminating the path of Dharma...',
      'Seeking guidance from the Rishis...',
      'Traversing the ocean of knowledge...',
      'Revealing eternal truths...'
    ],
    []
  );

  const readingMessages = useMemo(
    () => [
      'Opening the sacred manuscript...',
      'Turning the ancient pages...',
      'Preparing the narration...',
      'Unfolding the scripture...',
      'Setting the verse in order...',
      'Bringing the text to life...',
      'Entering the world of the text...',
      'Rendering the passage for you...',
      'Composing the narration...',
      'Breathing life into the words...',
    ],
    []
  );

  const cosmicMessages = mode === 'astro' ? astroMessages : mode === 'reading' ? readingMessages : generalMessages;

  useEffect(() => {
    if (!isLoading) return;
    setElapsedTime(0);
    const intervalId = window.setInterval(() => setElapsedTime(prev => prev + 1), 1000);
    return () => window.clearInterval(intervalId);
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) return;
    const intervalId = window.setInterval(() => {
      setMessageIndex(prev => (prev + 1) % cosmicMessages.length);
    }, 4000);
    return () => window.clearInterval(intervalId);
  }, [isLoading, cosmicMessages.length]);

  const progress = Math.min((elapsedTime / estimatedTime) * 100, 95);

  if (!isLoading) return null;

  return (
    <div className="cosmic-loader-container">
      <style>{`
        .cosmic-loader-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 18px 16px;
          background: linear-gradient(135deg, #fffaf5 0%, #fff5eb 50%, #fef3e2 100%);
          border-radius: 16px;
          margin-top: 12px;
          box-shadow:
            0 4px 20px rgba(234, 88, 12, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
          position: relative;
          overflow: hidden;
          width: min(100%, 420px);
        }

        .cosmic-loader-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(circle at 20% 80%, rgba(251, 146, 60, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(234, 88, 12, 0.08) 0%, transparent 50%);
          pointer-events: none;
        }

        .cosmic-ring-wrapper {
          position: relative;
          width: 120px;
          height: 120px;
          margin-bottom: 14px;
        }

        .cosmic-ring-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 4px solid rgba(234, 88, 12, 0.1);
        }

        .cosmic-ring-progress {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          transform: rotate(-90deg);
        }

        .cosmic-ring-progress circle {
          fill: none;
          stroke-width: 4;
          stroke-linecap: round;
          stroke: url(#cosmicGradient);
          transition: stroke-dashoffset 0.5s ease;
        }

        .cosmic-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .cosmic-sun {
          width: 42px;
          height: 42px;
          animation: cosmicPulse 2s ease-in-out infinite, cosmicRotate 20s linear infinite;
        }

        .cosmic-message {
          font-size: 15px;
          color: #9a3412;
          text-align: center;
          font-weight: 500;
          margin-bottom: 6px;
          min-height: 22px;
          animation: fadeInUp 0.5s ease;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .cosmic-subtext {
          font-size: 13px;
          color: #b45309;
          opacity: 0.8;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .cosmic-stars {
          position: absolute;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .cosmic-star {
          position: absolute;
          background: #f97316;
          border-radius: 50%;
          animation: twinkle 2s ease-in-out infinite;
        }

        .cosmic-star:nth-child(1) { top: 15%; left: 10%; width: 4px; height: 4px; animation-delay: 0s; }
        .cosmic-star:nth-child(2) { top: 25%; right: 15%; width: 3px; height: 3px; animation-delay: 0.5s; }
        .cosmic-star:nth-child(3) { bottom: 20%; left: 20%; width: 3px; height: 3px; animation-delay: 1s; }
        .cosmic-star:nth-child(4) { bottom: 30%; right: 10%; width: 4px; height: 4px; animation-delay: 1.5s; }
        .cosmic-star:nth-child(5) { top: 40%; left: 5%; width: 2px; height: 2px; animation-delay: 0.3s; }
        .cosmic-star:nth-child(6) { top: 60%; right: 8%; width: 2px; height: 2px; animation-delay: 0.8s; }

        .cosmic-orbit {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 104px;
          height: 104px;
          border: 1px dashed rgba(234, 88, 12, 0.2);
          border-radius: 50%;
          animation: cosmicRotate 15s linear infinite reverse;
        }

        .cosmic-planet {
          position: absolute;
          top: -4px;
          left: 50%;
          transform: translateX(-50%);
          width: 8px;
          height: 8px;
          background: linear-gradient(135deg, #fb923c, #ea580c);
          border-radius: 50%;
          box-shadow: 0 0 8px rgba(251, 146, 60, 0.5);
        }

        @keyframes cosmicPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.9; }
        }

        @keyframes cosmicRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .cosmic-progress-bar {
          width: 100%;
          max-width: 280px;
          height: 6px;
          background: rgba(234, 88, 12, 0.1);
          border-radius: 3px;
          overflow: hidden;
          margin-top: 12px;
        }

        .cosmic-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #fb923c, #ea580c, #f97316);
          background-size: 200% 100%;
          border-radius: 3px;
          transition: width 0.5s ease;
          animation: shimmer 2s linear infinite;
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .cosmic-scroll-icon {
          width: 38px;
          height: 38px;
          color: #f97316;
          margin-bottom: 12px;
          animation: cosmicPulse 2s ease-in-out infinite;
        }

        .cosmic-scroll-icon svg {
          width: 100%;
          height: 100%;
        }
      `}</style>

      {mode === 'astro' && (
        <div className="cosmic-stars">
          <div className="cosmic-star"></div>
          <div className="cosmic-star"></div>
          <div className="cosmic-star"></div>
          <div className="cosmic-star"></div>
          <div className="cosmic-star"></div>
          <div className="cosmic-star"></div>
        </div>
      )}

      {mode === 'astro' && (
        <div className="cosmic-ring-wrapper">
          <div className="cosmic-ring-bg"></div>
          <div className="cosmic-orbit">
            <div className="cosmic-planet"></div>
          </div>

          <svg className="cosmic-ring-progress" viewBox="0 0 140 140">
            <defs>
              <linearGradient id="cosmicGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fb923c" />
                <stop offset="50%" stopColor="#ea580c" />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
            </defs>
            <circle
              cx="70"
              cy="70"
              r="66"
              style={{
                strokeDasharray: `${2 * Math.PI * 66}`,
                strokeDashoffset: `${2 * Math.PI * 66 * (1 - progress / 100)}`
              }}
            />
          </svg>

          <div className="cosmic-center">
            <svg className="cosmic-sun" viewBox="0 0 64 64" fill="none">
              <g opacity="0.6">
                <line x1="32" y1="4" x2="32" y2="12" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
                <line x1="32" y1="52" x2="32" y2="60" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
                <line x1="4" y1="32" x2="12" y2="32" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
                <line x1="52" y1="32" x2="60" y2="32" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
                <line x1="12.2" y1="12.2" x2="17.9" y2="17.9" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
                <line x1="46.1" y1="46.1" x2="51.8" y2="51.8" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
                <line x1="12.2" y1="51.8" x2="17.9" y2="46.1" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
                <line x1="46.1" y1="17.9" x2="51.8" y2="12.2" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" />
              </g>
              <circle cx="32" cy="32" r="14" fill="url(#sunGradient)" />
              <defs>
                <radialGradient id="sunGradient" cx="0.3" cy="0.3" r="0.7">
                  <stop offset="0%" stopColor="#fcd34d" />
                  <stop offset="100%" stopColor="#f97316" />
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>
      )}

      {(mode === 'general' || mode === 'reading') && (
        <>
          <div className="cosmic-scroll-icon">
            {mode === 'reading' ? (
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 7v14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path d="M7 7H17M7 12H17M7 17H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            )}
          </div>

          <div className="cosmic-progress-bar">
            <div className="cosmic-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </>
      )}

      <p className="cosmic-message" key={messageIndex}>
        {cosmicMessages[messageIndex]}
      </p>

      <span className="cosmic-subtext">
        {mode === 'astro' ? 'Preparing your celestial insights' : mode === 'reading' ? 'Preparing your reading' : 'Preparing your spiritual insights'}
      </span>
    </div>
  );
};

export default CosmicLoader;
